import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { useCallback } from 'react';

import { LOCAL_MODE, localModeToken } from '../config/local-mode.js';

import { API_SCOPES } from './scopes.js';

/**
 * Hook returning a stable token-acquisition function. Tries silent first
 * and falls back to a redirect login when the cache is empty or stale.
 *
 * Local mode: returns a stub provider that yields `'local-mode'`. Callers
 * route to `useLocalToken` to keep the Rules-of-Hooks invariant — `LOCAL_MODE`
 * is build-time constant, so a given bundle only ever runs one branch.
 */
export function useToken(): () => Promise<string> {
  return LOCAL_MODE ? useLocalToken() : useRemoteToken();
}

function useLocalToken(): () => Promise<string> {
  return useCallback(() => localModeToken(), []);
}

function useRemoteToken(): () => Promise<string> {
  const { instance, accounts } = useMsal();
  return useCallback(async (): Promise<string> => {
    const account = accounts[0];
    if (!account) {
      await instance.loginRedirect({ scopes: [...API_SCOPES] });
      throw new Error('redirecting_to_login');
    }
    try {
      const result = await instance.acquireTokenSilent({
        account,
        scopes: [...API_SCOPES],
      });
      return result.accessToken;
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        await instance.acquireTokenRedirect({ scopes: [...API_SCOPES] });
        throw new Error('redirecting_for_token');
      }
      throw err;
    }
  }, [accounts, instance]);
}
